import { z } from "zod";
import type { StandardSchemaV1 } from "@tanstack/react-form";

/** The two grant-type configurations the server supports, as the comma-joined wire strings. */
export const GRANT_TYPE_CLIENT_CREDENTIALS = "client_credentials";
export const GRANT_TYPE_AUTHORIZATION_CODE = "authorization_code,refresh_token";

export function grantTypeLabel(authorizationGrantTypes: string): string {
	if (authorizationGrantTypes === GRANT_TYPE_AUTHORIZATION_CODE) {
		return messages["oAuthClient.authorizationGrantTypes.authorizationCode"];
	}
	return messages["oAuthClient.authorizationGrantTypes.clientCredentials"];
}

/** The server sends and receives redirect URIs as one newline-joined string. */
export function splitRedirectUris(redirectUris: string): string[] {
	return redirectUris
		.split(/\r?\n/)
		.map((value) => value.trim())
		.filter(Boolean);
}

/** Trims the field-array rows and drops the blank ones — the value that actually gets submitted. */
export function normalizeRedirectUris(redirectUris: string[]): string[] {
	return redirectUris.map((value) => value.trim()).filter(Boolean);
}

function isValidRedirectUri(value: string): boolean {
	try {
		return new URL(value).hash.length === 0;
	} catch {
		return false;
	}
}

/**
 * Per-row schema for the redirect-URI field array. Blank rows are allowed here (they are
 * dropped on submit); only non-blank rows must be absolute URLs without a fragment.
 */
export function createRedirectUriItemSchema() {
	return z
		.string()
		.trim()
		.refine((value) => value === "" || isValidRedirectUri(value), {
			error: messages["oAuthClient.redirectUris.invalid"],
		});
}

/** Shared name + redirectUris rules; redirect URIs are only validated for the authorization-code flow. */
function redirectUrisRefines<T extends { redirectUris: string[] }>(
	schema: z.ZodType<T>,
	usesAuthorizationCode: (value: T) => boolean,
) {
	return schema
		.refine(
			(value) => !usesAuthorizationCode(value) || normalizeRedirectUris(value.redirectUris).length > 0,
			{
				path: ["redirectUris"],
				error: messages["oAuthClient.redirectUris.required"],
			},
		)
		.refine(
			(value) => !usesAuthorizationCode(value) || normalizeRedirectUris(value.redirectUris).every(isValidRedirectUri),
			{
				path: ["redirectUris"],
				error: messages["oAuthClient.redirectUris.invalid"],
			},
		);
}

export interface OAuthClientCreateFormValues {
	oAuthClientName: string;
	/** `null` = the "please select" placeholder; the schema rejects it. */
	authorizationGrantTypes: string | null;
	redirectUris: string[];
}

export function createOAuthClientCreateSchema(): StandardSchemaV1<OAuthClientCreateFormValues> {
	return redirectUrisRefines(
		z.object({
			oAuthClientName: z.string().trim().min(1, { error: messages["validator.messages.required"] }),
			authorizationGrantTypes: z
				.string()
				.nullable()
				.refine((value) => value != null, { error: messages["validator.messages.required"] }),
			redirectUris: z.array(z.string()),
		}),
		(value) => value.authorizationGrantTypes === GRANT_TYPE_AUTHORIZATION_CODE,
	) as StandardSchemaV1<OAuthClientCreateFormValues>;
}

export interface OAuthClientEditFormValues {
	oAuthClientName: string;
	redirectUris: string[];
}

/** Cast for the same `strictNullChecks: false` reason as {@link createOAuthClientCreateSchema}. */
export function createOAuthClientEditSchema(usesAuthorizationCode: boolean): StandardSchemaV1<OAuthClientEditFormValues> {
	return redirectUrisRefines(
		z.object({
			oAuthClientName: z.string().trim().min(1, { error: messages["validator.messages.required"] }),
			redirectUris: z.array(z.string()),
		}),
		() => usesAuthorizationCode,
	) as StandardSchemaV1<OAuthClientEditFormValues>;
}
